type CorrectionType =
  | {
    title: string;
    type: "amount";
    amount: number;
  }
  | {
    title: string;
    type: "percent";
    target: "par amount" | "debt service";
    amount: number;
  };

export type Category = {
  type: "sources" | "uses";
  deletable: boolean;
  ticAdjusted: boolean;
  title: string;
  corrections: CorrectionType[];
};

/**
 * The three equivalent representations of a single correction's value, all
 * derived from the same underlying dollar amount.
 */
export type CorrectionValues = {
  /** Value in dollars. */
  dollar: number;
  /** Value as a percent of the total par amount (e.g. 10 for 10%). */
  parPercent: number;
  /** Value as a percent of the total debt service amount (e.g. 10 for 10%). */
  debtServicePercent: number;
};

/**
 * Resolves a correction to its canonical dollar value and the equivalent
 * par-amount / debt-service percentages, so all three can be displayed at once.
 *
 * @param correction Target correction.
 * @param parAmount Total par amount.
 * @param debtService Total debt service amount.
 */
export function resolveCorrectionValues(
  correction: CorrectionType,
  parAmount: number,
  debtService: number
): CorrectionValues {
  let dollar: number;
  if (correction.type === "amount") {
    dollar = correction.amount;
  } else if (correction.target === "par amount") {
    dollar = parAmount * correction.amount;
  } else {
    dollar = debtService * correction.amount;
  }

  return {
    dollar,
    parPercent: parAmount ? (dollar / parAmount) * 100 : 0,
    debtServicePercent: debtService ? (dollar / debtService) * 100 : 0,
  };
}

const DEFAULT_CATEGORIES: Category[] = [
  {
    type: "uses",
    deletable: false,
    ticAdjusted: true,
    title: "TIC Adjusting Expenses",
    corrections: [
      {
        title: "Underwriter's Discount",
        type: "percent",
        target: "par amount",
        amount: 0,
      },
      {
        title: "Bond Insurance",
        type: "percent",
        target: "debt service",
        amount: 0,
      },
      {
        title: "Cost of issuance",
        type: "amount",
        amount: 0,
      },
    ],
  },
];

export class SourcesAndUsesUtils {
  /**
   * Returns a copy of the default sources and uses categories.
   */
  public static getDefaultCategories(): Category[] {
    return [...DEFAULT_CATEGORIES];
  }

  /**
   * Calculates the total amount of money of a category
   *
   * @param category Target category.
   * @param parAmount Total par amount.
   * @param debtService Total debt service amount.
   */
  public static calculateCategoryAmount(
    category: Category,
    parAmount: number,
    debtService: number
  ): number {
    let result = 0;

    for (const correction of category.corrections) {
      if (correction.type === "amount") {
        result += correction.amount;
      } else if (correction.target === "par amount") {
        result += parAmount * correction.amount;
      } else if (correction.target === "debt service") {
        result += debtService * correction.amount;
      }
    }

    return result;
  }

  /**
   * Calculates the total amount of money of categories that are relevant
   * to the TIC.
   *
   * @param categories List of use/source categories configured.
   * @param parAmount Total par amount.
   * @param debtService Total debt service amount.
   */
  public static calculateTicAdjustedExpenses(
    categories: Category[],
    parAmount: number,
    debtService: number
  ): number {
    let result = 0;

    for (const category of categories) {
      if (category.type === "uses" && category.ticAdjusted) {
        result += this.calculateCategoryAmount(category, parAmount, debtService);
      }
    }

    return result;
  }

  /**
   * Calculates the total cost of issuing a bond series.
   *
   * @param categories List of use/source categories configured.
   * @param parAmount Total par amount.
   * @param debtService Total debt service amount.
   * @param capitalizedInterest Total amount of capitalized interest in dollars.
   */
  public static calculateTotalIssuanceCost(
    categories: Category[],
    parAmount: number,
    debtService: number,
    capitalizedInterest: number
  ): number {
    let result = capitalizedInterest;

    // Add all the uses
    for (const category of categories) {
      if (category.type !== "uses") continue;
      result += this.calculateCategoryAmount(category, parAmount, debtService);
    }

    return result;
  }

  /**
   * Calculates the total amount of money from the sources.
   *
   * @param categories List of use/source categories configured.
   * @param parAmount Total par amount.
   * @param production Total production amount.
   * @param debtService Total debt service amount.
   */
  public static calculateTotalSourceMoney(
    categories: Category[],
    parAmount: number,
    production: number,
    debtService: number
  ): number {
    let result = production;

    // Add all the uses
    for (const category of categories) {
      if (category.type !== "sources") continue;
      result += this.calculateCategoryAmount(category, parAmount, debtService);
    }

    return result;
  }

  /**
   * Calculates the total amount of project funds.
   *
   * @param categories List of use/source categories configured.
   * @param parAmount Total par amount.
   * @param production Total production amount.
   * @param debtService Total debt service amount.
   * @param capitalizedInterest Total amount of capitalized interest in dollars.
   */
  public static calculateProjectFunds(
    categories: Category[],
    parAmount: number,
    production: number,
    debtService: number,
    capitalizedInterest: number
  ) {
    const sourceMoney = this.calculateTotalSourceMoney(
      categories,
      parAmount,
      production,
      debtService
    );
    const issuanceCost = this.calculateTotalIssuanceCost(
      categories,
      parAmount,
      debtService,
      capitalizedInterest
    );
    return sourceMoney - issuanceCost;
  }
}
